import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, Trash2, Download, RefreshCw, Sparkles, Inbox, Ticket, Mail, Send, AlertTriangle } from 'lucide-react';
import { useBeta } from '@/contexts/BetaContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BetaAdminTab({ headers, language }) {
  const tt = (en, es) => (language === 'es' ? es : en);
  const { refresh: refreshBeta } = useBeta();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [genQty, setGenQty] = useState(10);
  const [genLabel, setGenLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLang, setInviteLang] = useState(language === 'es' ? 'es' : 'en');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    try {
      const [s, c, w] = await Promise.all([
        axios.get(`${API}/admin/beta-settings`, { headers }),
        axios.get(`${API}/admin/invite-codes`, { headers }),
        axios.get(`${API}/admin/waitlist`, { headers }),
      ]);
      setData(s.data);
      setCodes(c.data?.codes || []);
      setWaitlist(w.data?.waitlist || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Failed to load', 'Error al cargar'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleBeta = async (checked) => {
    try {
      await axios.put(`${API}/admin/beta-settings`, { beta_mode: checked }, { headers });
      toast.success(checked
        ? tt('Beta gating turned ON', 'Modo beta activado')
        : tt('Beta gating turned OFF — site is now public', 'Modo beta desactivado — sitio abierto al público'));
      refreshBeta();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const generateCodes = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/invite-codes/batch`, { quantity: genQty, label: genLabel || null }, { headers });
      toast.success(tt(`${r.data.created} codes generated`, `${r.data.created} códigos generados`));
      if (r.data.codes?.length) {
        try {
          await navigator.clipboard.writeText(r.data.codes.join('\n'));
          toast.info(tt('Codes copied to clipboard', 'Códigos copiados al portapapeles'));
        } catch {}
      }
      setGenLabel('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(tt(`Revoke code ${code}?`, `¿Revocar el código ${code}?`))) return;
    try {
      await axios.delete(`${API}/admin/invite-codes/${code}`, { headers });
      toast.success(tt('Revoked', 'Revocado'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const purgeDemoData = async () => {
    const confirmMsg = tt(
      'This will permanently delete the demo agency, 6 demo properties, 4 demo ads, the demo campaign and any featured listings linked to them. User-generated content is NOT touched. Continue?',
      'Esto eliminará permanentemente la agencia demo, 6 propiedades demo, 4 anuncios demo, la campaña demo y los destacados asociados. NO toca el contenido creado por usuarios. ¿Continuar?',
    );
    if (!window.confirm(confirmMsg)) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/admin/purge-demo-data`, {}, { headers });
      const d = r.data?.deleted || {};
      toast.success(tt(
        `Removed: ${d.agencies} agency, ${d.properties} properties, ${d.ads} ads, ${d.campaigns} campaign, ${d.featured_listings} featured.`,
        `Eliminado: ${d.agencies} agencia, ${d.properties} propiedades, ${d.ads} anuncios, ${d.campaigns} campaña, ${d.featured_listings} destacados.`,
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Purge failed', 'Error al limpiar'));
    } finally {
      setPurging(false);
    }
  };


  const sendInviteEmail = async () => {
    const email = (inviteEmail || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(tt('Enter a valid email', 'Ingresa un correo válido'));
      return;
    }
    setSendingInvite(true);
    try {
      const r = await axios.post(
        `${API}/admin/invite-codes/send-email`,
        { email, name: inviteName || null, language: inviteLang },
        { headers },
      );
      if (r.data?.delivered) {
        toast.success(tt(
          `Invite sent to ${email} (code ${r.data.code})`,
          `Invitación enviada a ${email} (código ${r.data.code})`,
        ));
      } else {
        toast.warning(tt(
          `Code ${r.data.code} created but email failed — copy and share manually.`,
          `Código ${r.data.code} creado pero el correo falló — cópialo y compártelo manualmente.`,
        ));
        try { await navigator.clipboard.writeText(r.data.code); } catch {}
      }
      setInviteEmail('');
      setInviteName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Failed to send invite', 'Error al enviar'));
    } finally {
      setSendingInvite(false);
    }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(tt('Copied', 'Copiado')); } catch {}
  };

  const downloadCsv = async () => {
    const t = headers?.Authorization?.split(' ')[1];
    const url = `${API}/admin/waitlist/csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wakacasa-waitlist.csv';
    try {
      const blob = await axios.get(url, { headers, responseType: 'blob' });
      const obj = URL.createObjectURL(blob.data);
      a.href = obj;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(obj);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-48" /></div>;

  const cc = data?.code_counts || {};

  return (
    <div className="space-y-6" data-testid="beta-admin-tab">
      {/* Master Toggle */}
      <Card className="border-2 border-[#D4AF37]/30 bg-gradient-to-br from-amber-50/30 to-white">
        <CardContent className="p-5 flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#0B192C]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading text-lg font-semibold text-[#0B192C]">
                  {tt('Beta Mode', 'Modo Beta')} {data?.beta_mode ? <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">ON</Badge> : <Badge className="ml-1 bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]">PUBLIC</Badge>}
                </p>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  {tt(
                    'When ON: anonymous visitors see the /beta page. Registration requires a valid invite code. The "BETA" badge appears next to the WakaCasa logo on every page.',
                    'Cuando está ACTIVO: visitantes anónimos ven la página /beta. El registro requiere un código de invitación válido. La insignia "BETA" aparece junto al logo en cada página.'
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2" data-testid="beta-toggle">
                <Label className="text-xs text-slate-600">{tt('Off', 'Desactivado')}</Label>
                <Switch checked={!!data?.beta_mode} onCheckedChange={toggleBeta} data-testid="beta-master-toggle" />
                <Label className="text-xs text-slate-600">{tt('On', 'Activado')}</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{tt('Active codes', 'Códigos activos')}</div><div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="stat-codes-active">{cc.active || 0}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{tt('Used codes', 'Códigos usados')}</div><div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="stat-codes-used">{cc.used || 0}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{tt('Revoked', 'Revocados')}</div><div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="stat-codes-revoked">{cc.revoked || 0}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{tt('Waitlist', 'Lista de espera')}</div><div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="stat-waitlist">{data?.waitlist_count || 0}</div></CardContent></Card>
      </div>

      {/* Generate codes */}
      <Card>
        <CardContent className="p-4">
          <p className="font-semibold text-sm text-[#0B192C] flex items-center gap-2 mb-3"><Ticket className="w-4 h-4" />{tt('Generate Invite Codes', 'Generar Códigos de Invitación')}</p>
          <div className="flex gap-2 flex-wrap items-end">
            <div>
              <Label className="text-xs">{tt('Quantity', 'Cantidad')}</Label>
              <Input type="number" min={1} max={200} value={genQty} onChange={e => setGenQty(Math.max(1, Math.min(200, Number(e.target.value) || 1)))} className="w-24 mt-1" data-testid="gen-qty" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs">{tt('Label (optional)', 'Etiqueta (opcional)')}</Label>
              <Input value={genLabel} onChange={e => setGenLabel(e.target.value)} placeholder={tt('e.g. Friends batch', 'ej. Tanda amigos')} className="mt-1" data-testid="gen-label" />
            </div>
            <Button onClick={generateCodes} disabled={generating} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="gen-btn">
              <Sparkles className="w-4 h-4 mr-1" />{tt('Generate', 'Generar')}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">{tt('New codes are auto-copied to your clipboard.', 'Los códigos nuevos se copian automáticamente al portapapeles.')}</p>
        </CardContent>
      </Card>

      {/* Send invite by email */}
      <Card data-testid="invite-email-card">
        <CardContent className="p-4">
          <p className="font-semibold text-sm text-[#0B192C] flex items-center gap-2 mb-1"><Mail className="w-4 h-4" />{tt('Send Invite by Email', 'Enviar Invitación por Correo')}</p>
          <p className="text-[11px] text-slate-500 mb-3">
            {tt(
              'Generates a single-use code and emails it directly to the recipient with a pre-filled signup link.',
              'Genera un código de un solo uso y lo envía al destinatario con un enlace de registro pre-llenado.'
            )}
          </p>
          <div className="grid sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">{tt('Recipient email', 'Correo del destinatario')} *</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                className="mt-1"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label className="text-xs">{tt('Recipient name (optional)', 'Nombre (opcional)')}</Label>
              <Input
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                placeholder={tt('Maria', 'María')}
                className="mt-1"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">{tt('Language', 'Idioma')}</Label>
              <select
                value={inviteLang}
                onChange={e => setInviteLang(e.target.value)}
                className="mt-1 h-10 w-full border border-slate-200 rounded-md bg-white px-2 text-sm"
                data-testid="invite-lang-select"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>
            <Button
              onClick={sendInviteEmail}
              disabled={sendingInvite || !inviteEmail}
              className="bg-[#0B192C] hover:bg-[#142944] text-[#D4AF37]"
              data-testid="invite-email-send-btn"
            >
              <Send className="w-4 h-4 mr-1" />{sendingInvite ? tt('Sending…', 'Enviando…') : tt('Send invite', 'Enviar invitación')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Codes table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 pb-2">
            <p className="font-semibold text-sm">{tt('Invite Codes', 'Códigos')} ({codes.length})</p>
            <Button size="sm" variant="ghost" onClick={load} className="h-8"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="overflow-x-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>{tt('Label', 'Etiqueta')}</TableHead>
                  <TableHead>{tt('Used by', 'Usado por')}</TableHead>
                  <TableHead>{tt('Created', 'Creado')}</TableHead>
                  <TableHead className="text-right">{tt('Actions', 'Acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(c => (
                  <TableRow key={c.code} data-testid={`code-row-${c.code}`}>
                    <TableCell className="font-mono font-bold tracking-wider">{c.code}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] capitalize ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : c.status === 'used' ? 'bg-slate-200 text-slate-600' : 'bg-red-100 text-red-700'}`}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{c.label || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{c.used_by?.email || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => copyCode(c.code)} className="h-7 px-2"><Copy className="w-3.5 h-3.5" /></Button>
                      {c.status === 'active' && (
                        <Button size="sm" variant="ghost" onClick={() => revokeCode(c.code)} className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`revoke-${c.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {codes.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{tt('No codes yet — generate one above', 'No hay códigos — genera arriba')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Waitlist */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 pb-2">
            <p className="font-semibold text-sm flex items-center gap-2"><Inbox className="w-4 h-4" />{tt('Waitlist', 'Lista de espera')} ({waitlist.length})</p>
            <Button size="sm" variant="outline" onClick={downloadCsv} className="h-8 gap-1" data-testid="waitlist-csv-btn">
              <Download className="w-3.5 h-3.5" />CSV
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[300px]">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Email</TableHead><TableHead>{tt('Name', 'Nombre')}</TableHead><TableHead>{tt('Joined', 'Se unió')}</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {waitlist.slice(0, 100).map((w, i) => (
                  <TableRow key={i} data-testid={`waitlist-row-${i}`}>
                    <TableCell className="text-xs">{w.email}</TableCell>
                    <TableCell className="text-xs">{w.name || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-400">{w.created_at ? new Date(w.created_at).toLocaleString() : ''}</TableCell>
                  </TableRow>
                ))}
                {waitlist.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-8">{tt('No waitlist signups yet', 'Aún no hay registros en la lista')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone — one-shot demo data purge */}
      <Card className="border border-red-200/60 bg-red-50/40" data-testid="purge-demo-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-red-900">{tt('Purge Demo Data', 'Eliminar Datos Demo')}</p>
              <p className="text-[11px] text-red-800/80 mt-1 max-w-2xl leading-relaxed">
                {tt(
                  'Removes the sample agency, 6 sample properties, 4 sample ads, the launch campaign and any featured listings the bootstrap script created. Real user-generated content is NEVER touched (matches by exact known names). Safe to run multiple times.',
                  'Elimina la agencia de muestra, 6 propiedades demo, 4 anuncios demo, la campaña de lanzamiento y los destacados creados por el script inicial. NO toca el contenido real de usuarios (filtra por nombres exactos). Es seguro ejecutarlo varias veces.',
                )}
              </p>
            </div>
            <Button
              onClick={purgeDemoData}
              disabled={purging}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 shrink-0"
              data-testid="purge-demo-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              {purging ? tt('Purging…', 'Eliminando…') : tt('Purge demo data', 'Eliminar datos demo')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
